import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Select, Label } from "@/admin/ui";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

const emailOf = (s) => {
  const m = (s || "").match(/<([^>]+)>/);
  return (m ? m[1] : s || "").trim();
};

const MailAdmin = () => {
  const [tab, setTab] = useState("inbox");
  const [emails, setEmails] = useState([]);
  const [open, setOpen] = useState(null);
  const [thread, setThread] = useState([]);
  const [addrs, setAddrs] = useState([]);
  const [compose, setCompose] = useState(null);
  const [label, setLabel] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [composePrompt, setComposePrompt] = useState("");
  const [composing, setComposing] = useState(false);
  const [tone, setTone] = useState("warm");
  const [template, setTemplate] = useState("editorial");

  const load = (folder, lbl = "") => api.get(`/admin/emails?folder=${folder}${lbl ? `&label=${lbl}` : ""}`).then((r) => setEmails(r.data)).catch(() => {});
  useEffect(() => { if (tab !== "compose") { load(tab, tab === "inbox" ? label : ""); setOpen(null); setThread([]); } }, [tab, label]);
  useEffect(() => { api.get("/admin/integrations").then((r) => setAddrs(r.data.email_addresses || [])).catch(() => {}); }, []);

  const labels = [...new Set(addrs.map((a) => (a.split("@")[0] || "").trim()).filter(Boolean))];

  const openMsg = async (e) => {
    setOpen(e);
    if (tab === "inbox" && !e.read) { api.patch(`/admin/emails/${e.id}/read`).catch(() => {}); }
    if (e.thread_id) {
      try { const { data } = await api.get(`/admin/email/thread/${e.thread_id}`); setThread(data); }
      catch { setThread([e]); }
    } else { setThread([e]); }
  };

  const reply = (draftHtml) => {
    setCompose({
      from_email: open.to || addrs[0] || "",
      to: emailOf(open.from),
      subject: open.subject?.toLowerCase().startsWith("re:") ? open.subject : `Re: ${open.subject || ""}`,
      html: draftHtml || "",
      thread_id: open.thread_id || null,
      in_reply_to: open.id,
    });
    setOpen(null); setThread([]); setTab("compose");
  };

  const checkDelivery = async () => {
    try { const { data } = await api.get(`/admin/email/status/${open.id}`); toast(`Delivery status: ${data.status}`); }
    catch { toast.error("Could not check delivery"); }
  };

  const del = async () => {
    if (!open || !window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/emails/${open.id}`);
      toast.success("Deleted");
      setOpen(null); setThread([]);
      load(tab, tab === "inbox" ? label : "");
    } catch { toast.error("Could not delete"); }
  };

  const aiReply = async () => {
    if (open.ai_draft && tone === "warm") { reply(open.ai_draft); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/ai/email-reply", { email_id: open.id, tone });
      reply(data.reply || "");
    } catch { toast.error("Could not draft a reply"); }
    setDrafting(false);
  };

  const aiCompose = async () => {
    if (!composePrompt.trim()) { toast("Tell the AI what to write."); return; }
    setComposing(true);
    try {
      const { data } = await api.post("/admin/ai/email-compose", { instruction: composePrompt, tone });
      setCompose((c) => ({ ...c, html: data.reply || "" }));
    } catch { toast.error("Could not draft the email"); }
    setComposing(false);
  };

  const send = async () => {
    if (!compose.from_email || !compose.to || !compose.subject) { toast("Fill from, to and subject."); return; }
    try {
      await api.post("/admin/email/send", { ...compose, template, html: (compose.html || "").replace(/\n/g, "<br>") });
      toast.success("Sent"); setCompose(null); setTab("sent");
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Send failed"); }
  };

  const TABS = [["inbox", "Inbox"], ["sent", "Sent"], ["compose", "Compose"]];

  return (
    <div className="max-w-3xl">
      <PageHead title="Mail" sub="Send from your addresses and reply to enquiries once your domain inbound is configured." />
      <div className="flex gap-6 border-b border-espresso/15 mb-6 text-[0.72rem] tracking-[0.14em] uppercase">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`mail-tab-${k}`} onClick={() => { setTab(k); if (k === "compose") setCompose({ from_email: addrs[0] || "", to: "", subject: "", html: "" }); }}
            className={`pb-3 border-b-2 -mb-px ${tab === k ? "border-espresso" : "border-transparent text-espresso/50"}`}>{l}</button>
        ))}
      </div>

      {tab === "compose" && compose && (
        <Card>
          {addrs.length ? (
            <Select label="From" value={compose.from_email} onChange={(v) => setCompose({ ...compose, from_email: v })} options={addrs} />
          ) : <p className="text-sm text-wine mb-4">Add sender addresses in Go Live settings first.</p>}
          <Select label="Template / vibe" value={template} onChange={setTemplate}
            options={[{ value: "editorial", label: "Editorial (dark header)" }, { value: "minimal", label: "Minimal (clean)" }, { value: "note", label: "Personal note (serif)" }, { value: "announcement", label: "Announcement (gold)" }]} />
          <Field label="To" value={compose.to} onChange={(v) => setCompose({ ...compose, to: v })} placeholder="recipient@email.com" />
          <Field label="Subject" value={compose.subject} onChange={(v) => setCompose({ ...compose, subject: v })} />
          <div className="mb-4 border border-espresso/15 bg-sand/40 p-3" data-testid="mail-ai-composer">
            <label className="block text-[0.62rem] tracking-[0.16em] uppercase text-espresso/50 mb-2">Write with AI</label>
            <div className="flex gap-2">
              <input value={composePrompt} onChange={(e) => setComposePrompt(e.target.value)} data-testid="mail-ai-prompt"
                placeholder="e.g. reply to a wholesale enquiry from a Lagos boutique"
                className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso" />
              <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="mail-compose-tone"
                className="bg-ivory border border-espresso/20 px-2 text-[0.66rem] uppercase tracking-[0.12em] outline-none">
                <option value="warm">Warm</option><option value="formal">Formal</option><option value="brief">Brief</option>
              </select>
              <Btn variant="ghost" onClick={aiCompose} disabled={composing} testid="mail-ai-compose">
                {composing ? <Loader2 size={13} className="animate-spin inline" /> : <Sparkles size={13} className="inline" />} Draft
              </Btn>
            </div>
          </div>
          <Field label="Message" textarea value={compose.html} onChange={(v) => setCompose({ ...compose, html: v })} />
          <Btn onClick={send} testid="mail-send">Send</Btn>
        </Card>
      )}

      {tab !== "compose" && (
        open ? (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => { setOpen(null); setThread([]); }} className="text-xs tracking-widest uppercase hover-underline">← Back</button>
              <div className="flex gap-2">
                {tab === "inbox" && (
                  <>
                    <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="mail-tone"
                      className="bg-ivory border border-espresso/20 px-2 py-1.5 text-[0.66rem] uppercase tracking-[0.12em] outline-none">
                      <option value="warm">Warm</option>
                      <option value="formal">Formal</option>
                      <option value="brief">Brief</option>
                    </select>
                    <Btn variant="ghost" onClick={aiReply} disabled={drafting} testid="mail-ai-reply">
                      {drafting ? <Loader2 size={13} className="animate-spin inline" /> : <Sparkles size={13} className="inline" />} {open.ai_draft && tone === "warm" ? "Use AI draft" : "Draft with AI"}
                    </Btn>
                  </>
                )}
                {tab === "sent" && <Btn variant="ghost" onClick={checkDelivery} testid="mail-check-delivery">Check delivery</Btn>}
                <Btn onClick={() => reply("")} testid="mail-reply">Reply</Btn>
                <Btn variant="danger" onClick={del} testid="mail-delete">Delete</Btn>
              </div>
            </div>
            <p className="font-serif text-2xl mb-5">{open.subject || "(no subject)"}</p>
            <div className="space-y-4">
              {thread.map((m) => (
                <div key={m.id} data-testid={`mail-thread-msg-${m.id}`}
                  className={`border-l-2 pl-4 py-1 ${m.folder === "sent" ? "border-gold" : "border-espresso/30"}`}>
                  <p className="text-xs text-espresso/50 mb-2">
                    {m.folder === "sent" ? `You → ${m.to}` : `${m.from} → ${m.to}`}
                    {m.auto ? " · auto-reply" : ""} · {m.at?.slice(0, 16).replace("T", " ")}
                  </p>
                  <div className="text-sm text-espresso/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: m.html || "" }} />
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="!p-0">
            {tab === "inbox" && labels.length > 0 && (
              <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-espresso/10">
                <button data-testid="mail-label-all" onClick={() => setLabel("")}
                  className={`text-[0.68rem] tracking-[0.12em] uppercase px-3 py-1 border ${label === "" ? "bg-espresso text-ivory border-espresso" : "border-espresso/25 text-espresso/60"}`}>All</button>
                {labels.map((l) => (
                  <button key={l} data-testid={`mail-label-${l}`} onClick={() => setLabel(l)}
                    className={`text-[0.68rem] tracking-[0.12em] uppercase px-3 py-1 border ${label === l ? "bg-espresso text-ivory border-espresso" : "border-espresso/25 text-espresso/60"}`}>{l}</button>
                ))}
              </div>
            )}
            {emails.length === 0 && <p className="p-8 text-espresso/40 text-sm">{tab === "inbox" ? "No mail here yet. Once your domain inbound points to /api/email/inbound, enquiries appear here." : "No sent mail yet."}</p>}
            {emails.map((e) => (
              <button key={e.id} onClick={() => openMsg(e)} data-testid={`mail-row-${e.id}`}
                className="w-full text-left flex items-center gap-4 px-5 py-3 border-b border-espresso/5 hover:bg-sand/40">
                <span className={`flex-1 ${!e.read && tab === "inbox" ? "font-semibold" : ""}`}>
                  <span className="text-sm">{tab === "inbox" ? e.from : e.to}{e.auto ? " · auto-reply" : ""}{tab === "inbox" && e.ai_draft ? " · AI reply ready" : ""}</span>
                  <span className="block text-xs text-espresso/50">{e.subject || "(no subject)"}</span>
                </span>
                <span className="text-xs text-espresso/40">{e.at?.slice(0, 10)}</span>
              </button>
            ))}
          </Card>
        )
      )}
    </div>
  );
};

export default MailAdmin;
